class VideoTestimonials {
  constructor(sectionElement) {
    this.section = sectionElement
    this.swiper = null
    this.videos = []
    this.currentVideoIndex = 0
    this.progressIntervals = new Map()
    this.sectionId = sectionElement.dataset.sectionId
    this.isVideoPlaying = false
    this.currentPlayingVideo = null
    this.titleSlideOffset = 1 // First slide is title, videos start at index 1

    this.init()
  }

  init() {
    if (typeof window.Swiper === "undefined") {
      console.warn("[v0] Swiper not loaded yet, retrying...")
      setTimeout(() => this.init(), 100)
      return
    }

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => this.initializeSlider())
    } else {
      this.initializeSlider()
    }
  }

  initializeSlider() {
    const swiperElement = this.section.querySelector(".video-testimonials-swiper")
    if (!swiperElement) {
      console.error("[v0] Swiper element not found")
      return
    }

    const settings = window.videoTestimonialsSettings?.[this.sectionId] || {}
    const slidesPerView = settings.slidesPerView || 3
    const slidesPerViewTablet = settings.slidesPerViewTablet || 2
    const slidesPerViewMobile = settings.slidesPerViewMobile || 1
    const spaceBetween = settings.spaceBetween || 20
    const autoplay = false
    const loop = false

    this.swiper = new window.Swiper(swiperElement, {
      slidesPerView: slidesPerViewMobile,
      spaceBetween: spaceBetween,
      loop: loop,
      centeredSlides: true,
      initialSlide: 1, // Start with first video slide (index 1)
      roundLengths: true,
      allowTouchMove: true, // Always allow touch/drag navigation
      autoplay: autoplay,
      breakpoints: {
        750: {
          slidesPerView: slidesPerViewTablet,
          spaceBetween: spaceBetween,
        },
        1024: {
          slidesPerView: slidesPerView,
          spaceBetween: spaceBetween,
        },
      },
      on: {
        init: () => {
          console.log("[v0] Swiper initialized")
          this.setupVideos()
          this.setupEventListeners()
          this.updateSlideCounter()
          this.handleSlideChange()
          setTimeout(() => {
            this.playVideo(0)
          }, 500)
        },
        slideChange: () => {
          this.handleSlideChange()
          this.updateSlideCounter()
        },
      },
    })
  }

  setupVideos() {
    this.videos = Array.from(this.section.querySelectorAll(".testimonial-video"))

    this.videos.forEach((video, index) => {
      video.controls = false
      video.muted = true
      video.playsInline = true

      video.addEventListener("loadedmetadata", () => {
        console.log(`[v0] Video ${index} metadata loaded, duration: ${video.duration}`)
      })

      video.addEventListener("timeupdate", () => {
        this.updateVideoProgress(video, index)
        if (video.duration > 0 && video.currentTime >= video.duration - 0.2) {
          if (!video.hasTriggeredEnd) {
            console.log(`[v0] Video ${index} near end detected via timeupdate - triggering end`)
            video.hasTriggeredEnd = true
            this.onVideoEnded(index)
          }
        }
      })

      video.addEventListener("ended", () => {
        console.log(`[v0] Video ${index} ended event fired`)
        if (!video.hasTriggeredEnd) {
          video.hasTriggeredEnd = true
          this.onVideoEnded(index)
        }
      })

      video.addEventListener("error", (e) => {
        console.error(`[v0] Video ${index} error:`, e)
      })

      video.addEventListener("click", (e) => {
        e.preventDefault()
        e.stopPropagation()
        console.log(`[v0] Video ${index} clicked directly`)

        if (this.currentPlayingVideo === video && !video.paused) {
          return
        }

        this.stopAllVideosAndReset()
        this.onVideoSlideClick(index)
      })
    })
  }

  setupEventListeners() {
    const prevBtn = this.section.querySelector(".prev-btn")
    const nextBtn = this.section.querySelector(".next-btn")

    if (prevBtn) {
      prevBtn.addEventListener("click", () => {
        if (this.swiper) {
          this.swiper.slidePrev()
        }
      })
    }

    if (nextBtn) {
      nextBtn.addEventListener("click", () => {
        if (this.swiper) {
          this.swiper.slideNext()
        }
      })
    }

    const videoSlides = this.section.querySelectorAll(".video-slide")
    videoSlides.forEach((slide, index) => {
      slide.addEventListener("click", (e) => {
        e.preventDefault()
        e.stopPropagation()
        console.log(`[v0] Video slide ${index} clicked`)
        if (index === 0) {
          console.log("[v0] Title slide clicked, ignoring")
          return
        }
        this.onVideoSlideClick(index)
      })
    })

    const watchBtns = this.section.querySelectorAll(".watch-full-btn")
    watchBtns.forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.stopPropagation()
        const videoIndex = Number.parseInt(btn.dataset.videoIndex)
        const youtubeUrl = btn.dataset.youtubeUrl

        if (youtubeUrl && youtubeUrl.trim() !== "") {
          this.openYouTubeModal(youtubeUrl)
        } else {
          this.onWatchFullVideo(videoIndex)
        }
      })
    })

    this.setupYouTubeModal()
  }

  handleSlideChange() {
    if (!this.swiper) {
      console.warn("[v0] Swiper not properly initialized")
      return
    }

    this.stopAllVideosAndReset()

    let activeIndex = this.swiper.activeIndex || 0

    if (activeIndex < 0) activeIndex = 0
    if (activeIndex >= this.videos.length + this.titleSlideOffset)
      activeIndex = this.videos.length + this.titleSlideOffset - 1

    console.log(`[v0] Active slide index: ${activeIndex}`)

    const slides = this.section.querySelectorAll(".video-slide")
    slides.forEach((slide, index) => {
      slide.classList.remove("active")
      if (index === activeIndex) {
        slide.classList.add("active")
      }
    })

    this.currentVideoIndex = activeIndex

    this.updateSlideCounter()

    if (activeIndex > 0) {
      const videoIndex = activeIndex - this.titleSlideOffset
      setTimeout(() => {
        this.playVideo(videoIndex)
      }, 300)
    }
  }

  onVideoSlideClick(index) {
    if (!this.swiper) {
      console.warn("[v0] Swiper not available for slide click")
      return
    }

    console.log(`[v0] Video slide ${index} clicked - sliding to index`)

    this.stopAllVideosAndReset()

    this.swiper.slideTo(index)

    if (index > 0) {
      const videoIndex = index - this.titleSlideOffset
      setTimeout(() => {
        this.playVideo(videoIndex)
      }, 300)
    }
  }

  playVideo(index) {
    if (index < 0 || index >= this.videos.length) {
      console.warn(`[v0] Invalid video index: ${index}`)
      return
    }

    const video = this.videos[index]
    if (!video) {
      console.warn(`[v0] Video at index ${index} not found`)
      return
    }

    console.log(`[v0] Playing video ${index}`)

    video.hasTriggeredEnd = false

    this.isVideoPlaying = true
    this.currentPlayingVideo = video
    if (this.swiper && this.swiper.autoplay) {
      this.swiper.autoplay.stop()
    }

    this.resetVideoProgress(index)

    const playPromise = video.play()
    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          console.log(`[v0] Video ${index} started playing`)
          this.startProgressTracking(video, index)
        })
        .catch((error) => {
          console.error(`[v0] Error playing video ${index}:`, error)
          this.isVideoPlaying = false
          this.currentPlayingVideo = null
        })
    }
  }

  stopAllVideosAndReset() {
    this.videos.forEach((video, index) => {
      if (video) {
        if (!video.paused) {
          video.pause()
          console.log(`[v0] Stopped video ${index}`)
        }
        video.currentTime = 0
        video.hasTriggeredEnd = false
        console.log(`[v0] Reset video ${index} time to 0`)
      }
      this.stopProgressTracking(index)
      this.resetVideoProgress(index)
    })

    this.isVideoPlaying = false
    this.currentPlayingVideo = null
  }

  stopAllVideos() {
    this.videos.forEach((video, index) => {
      if (video && !video.paused) {
        video.pause()
        console.log(`[v0] Stopped video ${index}`)
      }
      this.stopProgressTracking(index)
    })
  }

  updateVideoProgress(video, index) {
    if (!video || video.duration === 0) return

    const progress = (video.currentTime / video.duration) * 100
    const progressBar = this.section.querySelector(
      `.video-slide:nth-child(${index + this.titleSlideOffset + 1}) .progress-fill`,
    )

    if (progressBar) {
      progressBar.style.width = `${progress}%`
    }
  }

  startProgressTracking(video, index) {
    this.stopProgressTracking(index)

    const interval = setInterval(() => {
      if (video.paused || video.ended) {
        this.stopProgressTracking(index)
        return
      }
      this.updateVideoProgress(video, index)
    }, 100)

    this.progressIntervals.set(index, interval)
  }

  stopProgressTracking(index) {
    const interval = this.progressIntervals.get(index)
    if (interval) {
      clearInterval(interval)
      this.progressIntervals.delete(index)
    }
  }

  resetVideoProgress(index) {
    const progressBar = this.section.querySelector(
      `.video-slide:nth-child(${index + this.titleSlideOffset + 1}) .progress-fill`,
    )
    if (progressBar) {
      progressBar.style.width = "0%"
    }
  }

  onVideoEnded(index) {
    console.log(`[v0] Video ${index} ended - starting auto-advance sequence`)
    console.log(`[v0] Current video count: ${this.videos.length}, current index: ${index}`)

    this.stopProgressTracking(index)
    this.isVideoPlaying = false
    this.currentPlayingVideo = null

    if (this.swiper && index < this.videos.length - 1) {
      const nextSlideIndex = index + 1 + this.titleSlideOffset
      console.log(`[v0] Auto-advancing to next slide: ${nextSlideIndex} (video index: ${index + 1})`)

      // Move to next slide immediately
      this.swiper.slideTo(nextSlideIndex, 300) // 300ms transition

      // Play the next video after a short delay
      setTimeout(() => {
        console.log(`[v0] Playing next video: ${index + 1}`)
        this.playVideo(index + 1)
      }, 400)
    } else if (index === this.videos.length - 1) {
      console.log("[v0] Last video ended - sequence complete")
      // Optionally restart from beginning or stay on last slide
    } else {
      console.warn(`[v0] Unexpected state in onVideoEnded: index=${index}, videosLength=${this.videos.length}`)
    }
  }

  onWatchFullVideo(index) {
    console.log(`[v0] Watch full video ${index} clicked`)
    if (this.swiper) {
      this.swiper.slideTo(index)
    }
  }

  setupYouTubeModal() {
    const modal = document.getElementById("youtube-modal")
    const closeBtn = modal.querySelector(".modal-close")
    const backdrop = modal.querySelector(".modal-backdrop")

    if (closeBtn) {
      closeBtn.addEventListener("click", () => {
        this.closeYouTubeModal()
      })
    }

    if (backdrop) {
      backdrop.addEventListener("click", () => {
        this.closeYouTubeModal()
      })
    }

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && modal.style.display !== "none") {
        this.closeYouTubeModal()
      }
    })
  }

  openYouTubeModal(youtubeUrl) {
    const modal = document.getElementById("youtube-modal")
    const iframe = document.getElementById("youtube-iframe")

    if (!modal || !iframe) {
      console.error("[v0] YouTube modal elements not found")
      return
    }

    const embedUrl = this.convertToEmbedUrl(youtubeUrl)

    if (!embedUrl) {
      console.error("[v0] Invalid YouTube URL:", youtubeUrl)
      return
    }

    this.stopAllVideos()

    iframe.src = embedUrl

    modal.style.display = "flex"
    document.body.style.overflow = "hidden"

    requestAnimationFrame(() => {
      modal.classList.add("show")
    })

    console.log("[v0] YouTube modal opened:", embedUrl)
  }

  closeYouTubeModal() {
    const modal = document.getElementById("youtube-modal")
    const iframe = document.getElementById("youtube-iframe")

    if (!modal || !iframe) return

    modal.classList.remove("show")

    setTimeout(() => {
      iframe.src = ""
      modal.style.display = "none"
      document.body.style.overflow = ""
    }, 500)

    console.log("[v0] YouTube modal closed")
  }

  convertToEmbedUrl(youtubeUrl) {
    if (!youtubeUrl || typeof youtubeUrl !== "string") return null

    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /youtube\.com\/watch\?.*v=([^&\n?#]+)/,
    ]

    let videoId = null
    for (const pattern of patterns) {
      const match = youtubeUrl.match(pattern)
      if (match && match[1]) {
        videoId = match[1]
        break
      }
    }

    if (!videoId) return null

    return `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`
  }

  updateSlideCounter() {
    const currentSlideEl = this.section.querySelector(".current-slide")
    if (!currentSlideEl || !this.swiper) return

    let activeIndex = this.swiper.activeIndex

    if (typeof activeIndex === "undefined" || activeIndex === null) {
      activeIndex = this.currentVideoIndex || 0
    }

    if (activeIndex < 0) activeIndex = 0
    if (activeIndex >= this.videos.length + this.titleSlideOffset)
      activeIndex = this.videos.length + this.titleSlideOffset - 1

    this.currentVideoIndex = activeIndex

    const displayIndex = (activeIndex + 1).toString().padStart(2, "0")
    currentSlideEl.textContent = displayIndex

    console.log(`[v0] Counter updated to: ${displayIndex} (activeIndex: ${activeIndex})`)
  }

  destroy() {
    this.stopAllVideos()
    this.progressIntervals.forEach((interval) => clearInterval(interval))
    this.progressIntervals.clear()

    this.closeYouTubeModal()

    if (this.swiper) {
      this.swiper.destroy(true, true)
      this.swiper = null
    }
  }
}

document.addEventListener("DOMContentLoaded", () => {
  const videoSections = document.querySelectorAll(".video-testimonials-section")

  videoSections.forEach((section) => {
    try {
      new VideoTestimonials(section)
      console.log("[v0] Video testimonials section initialized")
    } catch (error) {
      console.error("[v0] Error initializing video testimonials:", error)
    }
  })
})

document.addEventListener("visibilitychange", () => {
  if (document.hidden) {
    document.querySelectorAll(".testimonial-video").forEach((video) => {
      if (!video.paused) {
        video.pause()
      }
    })
  }
})
